import type { Key } from "react";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import type { ActionButton, ColumnType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    ALLOWED_CSV_EXTENSIONS,
    ALLOWED_CSV_MIME_TYPES,
    INITIAL_TABLE_STATE,
    TABLE_PAGE_SIZE,
    promptsTableReducer,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/consts";
import { useAddPromptModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useAddPromptModal";
import { useAutoGenerationModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useAutoGenerationModal";
import { useBulkChangeTopicModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useBulkChangeTopicModal";
import { useDeletePromptsModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useDeletePromptsModal";
import { LoadingBanner } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/LoadingBanner";
import { TopicEmptyState } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/TopicEmptyState";
import type {
    IModifiablePrompt,
    IModifiableTopic,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { getMaxPrompts } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { SortDirection } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

import { getColumnConfig } from "./config";
import { PromptCreationDropdown } from "./PromptCreationDropdown";
import { TableWrapper } from "./PromptsTable.styled";
import { getColumnSorter } from "./utils";

interface IPromptsTableProps {
    allTopics: IModifiableTopic[];
    actualTopics: IModifiableTopic[];
    selectedTopic: string;
    totalPromptsCount: number;
    isLoading: boolean;
    error: boolean;
    onDeletePrompts: (prompts: IModifiablePrompt[]) => void;
    onEditPrompts: (prompts: IModifiablePrompt[], newTopic: string) => void;
    onCreatePrompt: (text: string, topic: string) => void;
    onCSVUpload: (files: File[]) => Promise<void>;
    onGeneratePrompts?: (topic: string, count: number) => Promise<void>;
    onManageLaterClick?: () => void;
}

export const PromptsTable = ({
    allTopics,
    actualTopics,
    selectedTopic,
    totalPromptsCount,
    isLoading,
    error,
    onDeletePrompts,
    onEditPrompts,
    onCreatePrompt,
    onCSVUpload,
    onGeneratePrompts,
    onManageLaterClick,
}: IPromptsTableProps) => {
    const [tableState, tableDispatch] = useReducer(promptsTableReducer, INITIAL_TABLE_STATE);
    const { searchTerm, currentPage, selectedRowKeys } = tableState;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const displayedPrompts = useMemo<IModifiablePrompt[]>(() => {
        const { dataIndex, sortDirection } = tableState.sorting;
        const trimmedSearchTerm = searchTerm.toLowerCase().trim();
        const currentTopic = allTopics.find(({ name }) => name === selectedTopic);
        const prompts = trimmedSearchTerm
            ? (currentTopic?.prompts.filter(({ query }) => query.toLowerCase().includes(trimmedSearchTerm)) ?? [])
            : (currentTopic?.prompts ?? []);

        const sorter = getColumnSorter(dataIndex);
        const direction = sortDirection === "descend" ? -1 : 1;
        return prompts.toSorted((a, b) => sorter(a, b) * direction);
    }, [allTopics, selectedTopic, searchTerm, tableState.sorting]);

    useEffect(() => {
        if (!selectedRowKeys.length) {
            return;
        }

        const visibleIds = new Set<Key>(displayedPrompts.map((p) => p.promptId));
        const filtered = selectedRowKeys.filter((key) => visibleIds.has(key));

        if (filtered.length !== selectedRowKeys.length) {
            tableDispatch({ type: "SET_SELECTION", payload: filtered });
        }
    }, [displayedPrompts]);

    const allowedCSVFileExtensions = useMemo<string>(
        () => [...ALLOWED_CSV_EXTENSIONS.map((extension) => `.${extension}`), ...ALLOWED_CSV_MIME_TYPES].join(","),
        [],
    );

    const actualTopicNames = useMemo<string[]>(
        () => actualTopics?.reduce((acc, { name }) => [...acc, name], [] as string[]) ?? [],
        [actualTopics],
    );

    const maxPrompts = getMaxPrompts();
    const promptsUsagePercent = totalPromptsCount / maxPrompts;
    const isCreationEnabled = !isLoading && promptsUsagePercent < 1;
    const allPrompts = useMemo<string[]>(
        () =>
            actualTopics?.reduce(
                (acc, { prompts }) => [...acc, ...prompts.map(({ query }) => query)],
                [] as string[],
            ) ?? [],
        [actualTopics],
    );

    const { openModal: openDeleteModal, modal: deleteModal } = useDeletePromptsModal((prompts) => {
        onDeletePrompts(prompts);
        tableDispatch({ type: "CLEAR_SELECTION" });
    });

    const { openModal: openBulkChangeTopicModal, modal: bulkChangeTopicModal } = useBulkChangeTopicModal(
        actualTopicNames,
        (prompts, newTopic) => {
            onEditPrompts(prompts, newTopic);
            tableDispatch({ type: "CLEAR_SELECTION" });
        },
    );

    const { openModal: openAutoGenerationModal, modal: autoGenerationModal } = useAutoGenerationModal(
        actualTopicNames,
        totalPromptsCount,
        maxPrompts,
        onGeneratePrompts ?? (() => Promise.resolve()),
    );

    const { openModal: openAddModal, modal: addModal } = useAddPromptModal(
        actualTopicNames,
        allPrompts,
        onCreatePrompt,
    );

    const getSelectedPrompts = useCallback(
        (): IModifiablePrompt[] => displayedPrompts.filter((prompt) => selectedRowKeys.includes(prompt.promptId)),
        [displayedPrompts, selectedRowKeys],
    );

    const handleManualCreation = useCallback((): void => {
        const topic = selectedTopic !== ALL_TOPICS_NAME ? selectedTopic : (actualTopicNames?.[0] ?? "");
        openAddModal(topic);
    }, [selectedTopic, actualTopicNames, openAddModal]);

    const handleAutoGeneration = useCallback((): void => {
        const topic = selectedTopic !== ALL_TOPICS_NAME ? selectedTopic : (actualTopicNames?.[0] ?? "");
        openAutoGenerationModal(topic);
    }, [selectedTopic, actualTopicNames, openAutoGenerationModal]);

    const handleDelete = useCallback((prompt: IModifiablePrompt): void => openDeleteModal([prompt]), [openDeleteModal]);

    const handleBulkDelete = useCallback(
        (): void => openDeleteModal(getSelectedPrompts()),
        [getSelectedPrompts, openDeleteModal],
    );

    const handleBulkChangeTopic = useCallback(
        (): void => openBulkChangeTopicModal(getSelectedPrompts()),
        [getSelectedPrompts, openBulkChangeTopicModal],
    );

    const handleSelectionChange = useCallback(
        (keys: Key[]): void => tableDispatch({ type: "SET_SELECTION", payload: keys }),
        [],
    );

    const selectionActionButtons = useMemo<ActionButton[]>(
        () => [
            {
                id: "prompts-table-delete-selected",
                iconName: "delete",
                children: translate("llm.prompts.edit.prompts-table.bulk-actions.delete"),
                onClick: handleBulkDelete,
                isDisabled: selectedRowKeys.length >= totalPromptsCount,
            },
            {
                id: "prompts-table-change-topic-selected",
                iconName: "refresh",
                children: translate("llm.prompts.edit.prompts-table.bulk-actions.topic"),
                onClick: handleBulkChangeTopic,
            },
        ],
        [handleBulkChangeTopic, handleBulkDelete, selectedRowKeys.length, totalPromptsCount],
    );

    const handleSort = (sorterData: { columnKey: keyof IModifiablePrompt; order: SortDirection }) => {
        tableDispatch({
            type: "SET_SORTING",
            payload: { dataIndex: sorterData.columnKey, sortDirection: sorterData.order },
        });
    };

    const handleSearch = (term: string) => {
        tableDispatch({
            type: "SET_SEARCH",
            payload: term,
        });
    };

    const handlePageChange = (pageSize: number, total: number, page: number) => {
        tableDispatch({
            type: "SET_PAGE",
            payload: page,
        });
    };

    const handleEditPrompt = useCallback(
        (prompt: IModifiablePrompt, newTopic: string): void => onEditPrompts([prompt], newTopic),
        [onEditPrompts],
    );

    const columnsConfig = useMemo<Array<ColumnType<IModifiablePrompt>>>(
        () => getColumnConfig(handleEditPrompt, handleDelete, tableState.sorting, actualTopicNames, totalPromptsCount),
        [handleEditPrompt, handleDelete, tableState.sorting, actualTopicNames, totalPromptsCount],
    );

    const onCSVFileInputChange = async (ev: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const files = Array.from(ev.target.files ?? []);
        if (files.length) {
            await onCSVUpload(files);
        }

        ev.target.value = "";
    };

    const onCSVUploadClick = useCallback((): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "add prompts/upload csv");
        fileInputRef.current?.click();
    }, []);

    const EmptyStateComponent = useCallback(
        () => (
            <TopicEmptyState
                onAutoGenerate={isCreationEnabled && onGeneratePrompts ? handleAutoGeneration : undefined}
                onManualCreation={isCreationEnabled ? handleManualCreation : undefined}
            />
        ),
        [isCreationEnabled, handleAutoGeneration, handleManualCreation, onGeneratePrompts],
    );

    return (
        <Flex
            flexDirection="column"
            width="100%"
        >
            {isLoading && onManageLaterClick ? (
                <Box
                    padding="12px"
                    borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
                >
                    <LoadingBanner onManageLater={onManageLaterClick} />
                </Box>
            ) : null}
            <TableWrapper
                height="100%"
                hasData={!!displayedPrompts?.length}
                hasPagination={displayedPrompts?.length >= TABLE_PAGE_SIZE}
            >
                <Table
                    key={`${selectedTopic}-${totalPromptsCount}`}
                    id="prompts-table"
                    size="large"
                    rowHoverable
                    sticky
                    columnConfig={columnsConfig}
                    data={displayedPrompts}
                    rowKey={(record: IModifiablePrompt) => record.promptId}
                    theme="gray"
                    isLoading={isLoading}
                    loaderRowCount={TABLE_PAGE_SIZE}
                    onSort={handleSort}
                    selection={{
                        enabled: true,
                        columnWidth: 24,
                        selectedRowKeys,
                        onChange: handleSelectionChange,
                    }}
                    pagination={{
                        pageSize: TABLE_PAGE_SIZE,
                        current: currentPage,
                        onChange: handlePageChange,
                    }}
                    noData={
                        error
                            ? {
                                  title: translate("common.error"),
                                  subtitle: translate("common.error.subtitle"),
                              }
                            : {
                                  Component: EmptyStateComponent,
                              }
                    }
                >
                    <Table.TableTop>
                        <Table.TableTop.Title
                            title={translate("llm.prompts.edit.prompts-table.title")}
                            extensions={
                                <PromptCreationDropdown
                                    isCreationEnabled={isCreationEnabled}
                                    isLoading={isLoading}
                                    canGeneratePrompts={!!onGeneratePrompts}
                                    handleManualCreation={handleManualCreation}
                                    handleAutoGeneration={handleAutoGeneration}
                                    onCSVUploadClick={onCSVUploadClick}
                                />
                            }
                        />
                        <Table.TableTop.Selection
                            selectText={translate("llm.prompts.edit.prompts-table.multiselect-title")}
                            actionButtons={selectionActionButtons}
                            selectAllDropdown={{
                                totalItemsCount: displayedPrompts.length,
                                onSelectAllAcrossPages: () =>
                                    tableDispatch({
                                        type: "SET_SELECTION",
                                        payload: displayedPrompts.map((prompt) => prompt.promptId),
                                    }),
                                onClearAllSelection: () => tableDispatch({ type: "CLEAR_SELECTION" }),
                            }}
                        />
                        <Table.TableTop.Filters
                            filters={[
                                {
                                    filter: "search",
                                    placeholder: translate("forms.search.placeholder"),
                                    onSearchChange: handleSearch,
                                },
                            ]}
                        />
                    </Table.TableTop>
                </Table>
            </TableWrapper>
            <input
                ref={fileInputRef}
                type="file"
                accept={allowedCSVFileExtensions}
                onChange={onCSVFileInputChange}
                data-automation="prompts-table-csv-file-input"
                hidden
            />
            {addModal}
            {autoGenerationModal}
            {deleteModal}
            {bulkChangeTopicModal}
        </Flex>
    );
};
